import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setStructure } from 'features/structure/structure-slice';
import { useEffect, useRef } from 'react';
import {
  buildStructureFromPlan,
  deriveHybridAutobuildPlan
} from 'utils/hybrid-structure-autobuild';
import { isHybridOnlySystemPreference } from 'utils/project';

const AUTOBUILD_TABS: ReadonlySet<string> = new Set([
  'existingEquipment',
  'equipmentPreferences',
  'structure'
]);

export default function useHybridStructureAutobuild(): void {
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((state) => state.ui.activeTab);
  const isHybrid = useAppSelector((state) =>
    isHybridOnlySystemPreference(
      state.project.projectSettings.find(
        (ps) => ps.code === 'system_preference'
      )?.value
    )
  );
  const structureLength = useAppSelector(
    (state) => state.structure.structure.length
  );
  const structureUniqueId = useAppSelector(
    (state) => state.structure.structureUniqueId
  );
  const itemsLoaded = useAppSelector((state) => state.data.items.length > 0);
  const levelsLoading = useAppSelector((state) => state.ai.levelsLoading);
  const attomData = useAppSelector((state) => state.project.attomData);
  const projectSettings = useAppSelector(
    (state) => state.project.projectSettings
  );

  const hasBuiltRef = useRef(false);

  useEffect(() => {
    if (!AUTOBUILD_TABS.has(activeTab)) {
      hasBuiltRef.current = false;
      return;
    }
    if (hasBuiltRef.current) return;
    if (!isHybrid) return;
    if (structureLength !== 0) return;
    if (!structureUniqueId || !itemsLoaded || levelsLoading) return;

    const settingValue = (code: string) =>
      projectSettings.find((ps) => ps.code === code)?.value;

    const plan = deriveHybridAutobuildPlan({
      attomData,
      squareFootage: settingValue('square_footage'),
      numberOfFloors: settingValue('number_of_floors'),
      foundationType: settingValue('foundation_type')
    });

    const levels = buildStructureFromPlan(plan);
    if (!levels || levels.length === 0) return;

    hasBuiltRef.current = true;
    // eslint-disable-next-line no-console
    console.debug('[hybrid-autobuild]', plan.derivation);
    dispatch(setStructure(levels));
  }, [
    activeTab,
    isHybrid,
    structureLength,
    structureUniqueId,
    itemsLoaded,
    levelsLoading,
    attomData,
    projectSettings,
    dispatch
  ]);
}
